import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, 
  Trash2, 
  Save, 
  Send,
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  RefreshCw,
  X,
  List,
  AlignLeft
} from 'lucide-react';
import { timesheetAPI, projectAPI } from '../services/api';
import { format, addDays, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

const EditTimesheet = () => {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const [descriptionFormats, setDescriptionFormats] = useState({});

  // Helper function to safely format dates
  const safeFormatDate = (dateString, formatString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isValid(date)) {
        return format(date, formatString);
      }
      return 'Invalid Date';
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const { data: timesheet, isLoading, error } = useQuery(
    ['timesheet', id],
    () => timesheetAPI.getTimesheetById(id),
    {
      select: (response) => {
        // Handle nested data structure
        const actualData = response.data?.data || response.data;
        return actualData;
      },
      enabled: !!id,
      onError: (error) => {
        toast.error('Failed to load timesheet: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const { register, control, handleSubmit, watch, reset, formState: { errors } } = useForm();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries'
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedEntries = watch('entries') || [];

  // Fetch assigned projects
  const { data: projects, isLoading: projectsLoading, error: projectsError } = useQuery(
    'my-assigned-projects',
    () => projectAPI.getMyAssignedProjects(),
    {
      select: (response) => response.data.projects,
    }
  );

  const updateMutation = useMutation(
    (data) => timesheetAPI.updateTimesheet(id, data),
    {
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries('timesheets');
        queryClient.invalidateQueries(['timesheet', id]);
        queryClient.invalidateQueries('admin-timesheets');
        // Use the updated timesheet status from the response
        const updatedStatus = data?.timesheet?.status || data?.data?.timesheet?.status;
        const message = timesheet?.status === 'PENDING' 
          ? 'Timesheet corrected and resubmitted for approval' 
          : timesheet?.status === 'REUPDATED'
          ? 'Timesheet updated and resubmitted for approval'
          : updatedStatus === 'REUPDATED'
          ? 'Timesheet updated and resubmitted for approval'
          : 'Timesheet updated successfully';
        toast.success(message);
        
        // Create notification for admins, super admins, and managers if resubmitted
        if (timesheet?.status === 'PENDING' || updatedStatus === 'REUPDATED') {
          const notification = {
            id: Date.now(),
            title: 'Timesheet Resubmitted',
            message: `${user?.firstName} ${user?.lastName} resubmitted a timesheet for review`,
            type: 'timesheet_submitted',
            isRead: false,
            createdAt: new Date().toISOString(),
            employeeName: `${user?.firstName} ${user?.lastName}`,
            timesheetId: id
          };
          
          addNotification(notification);
        }
        
        navigate('/timesheets');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update timesheet');
      },
    }
  );

  const submitMutation = useMutation(
    () => timesheetAPI.submitTimesheet(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('timesheets');
        queryClient.invalidateQueries(['timesheet', id]);
        toast.success('Timesheet submitted successfully');
        navigate('/timesheets');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit timesheet');
      },
    }
  );

  useEffect(() => {
    if (timesheet && projects) {
      const mappedEntries = timesheet.entries?.map((entry) => {
        // Try multiple ways to get the project ID
        let projectId = '';
        let projectName = '';
        
        if (entry.project?.id) {
          projectId = String(entry.project.id);
          projectName = entry.project.name || '';
        } else if (entry.projectId) {
          projectId = String(entry.projectId);
          projectName = entry.projectName || '';
        } else if (entry.project?.name && projects) {
          // Try to find project by name if ID is not available
          const matchingProject = projects.find(p => p.name === entry.project.name);
          if (matchingProject) {
            projectId = String(matchingProject.id);
            projectName = matchingProject.name;
          }
        } else if (entry.projectName && projects) {
          // Try to find project by projectName field
          const matchingProject = projects.find(p => p.name === entry.projectName);
          if (matchingProject) {
            projectId = String(matchingProject.id);
            projectName = matchingProject.name;
          }
        }
        
        return {
          entryDate: entry.date || entry.entryDate,
          hoursWorked: entry.hours || entry.hoursWorked,
          projectId: projectId,
          projectName: projectName,
          taskDescription: entry.task || entry.taskDescription || '',
        };
      }) || [];
      
      reset({
        startDate: timesheet.startDate,
        endDate: timesheet.endDate,
        description: timesheet.description || '',
        entries: mappedEntries
      });

      const initialFormats = {};
      mappedEntries.forEach((entry, index) => {
        const description = entry.taskDescription || '';
        if (description.trim().startsWith('•') || description.includes('\n•') || description.trim().startsWith('-')) {
          initialFormats[index] = 'bullets';
        }
      });
      setDescriptionFormats(initialFormats);
    }
  }, [timesheet, reset, projects]);


  // Allow editing for DRAFT (including revoked), PENDING, REUPDATED, and REJECTED timesheets
  const isRevokedDraft = timesheet?.status === 'DRAFT' && timesheet?.revokedBy != null;
  const canEdit = timesheet?.status === 'DRAFT' || 
                  timesheet?.status === 'PENDING' || 
                  timesheet?.status === 'REUPDATED' || 
                  timesheet?.status === 'REJECTED' || 
                  isRevokedDraft;
  const canSubmit = timesheet?.status === 'DRAFT';
  const canSubmitRejectedOrRevoked = timesheet?.status === 'REJECTED' || isRevokedDraft;

  const addEntryForDate = (date) => {
    if (!canEdit) return;
    append({
      entryDate: date,
      hoursWorked: 8,
      projectId: '',
      projectName: '',
      taskDescription: '',
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    // Get current date in IST by using local time (assuming server sets IST)
    // Format as YYYY-MM-DD for date input compatibility
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTotalHours = () => {
    return watchedEntries.reduce((total, entry) => {
      const hours = parseFloat(entry?.hoursWorked) || 0;
      return total + hours;
    }, 0);
  };

  const getEntriesByDate = () => {
    const grouped = {};
    watchedEntries.forEach((entry, index) => {
      const date = entry?.entryDate;
      if (date) {
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push({ ...entry, index });
      }
    });
    return grouped;
  };

  const getDateRange = () => {
    const startDate = watchedStartDate ? new Date(watchedStartDate) : null;
    const endDate = watchedEndDate ? new Date(watchedEndDate) : null;

    if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate) || startDate > endDate) {
      return [];
    }

    const dates = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  };

  const toggleDescriptionFormat = (entryIndex, formatType) => {
    if (!canEdit) return;
    setDescriptionFormats((prev) => ({
      ...prev,
      [entryIndex]: formatType,
    }));
  };

  const getDescriptionFormat = (entryIndex) => {
    return descriptionFormats[entryIndex] || 'paragraph';
  };

  const handleRemoveEntry = (entryIndex) => {
    if (!canEdit) return;
    remove(entryIndex);
  };

  const onSubmit = async (data, shouldSubmit = false) => {
    // Validate that entries with hours or descriptions have a project selected
    const entriesWithData = Array.isArray(data.entries) ? data.entries.filter(entry => {
      const hours = parseFloat(entry.hoursWorked);
      const hasHours = !Number.isNaN(hours) && hours > 0;
      const hasDescription = entry.taskDescription && entry.taskDescription.trim().length > 0;
      return hasHours || hasDescription;
    }) : [];

    // Check if any entry with data is missing a project
    const missingProject = entriesWithData.some(entry => {
      return !entry.projectId && !entry.projectName;
    });

    if (missingProject) {
      toast.error('Please select a project for all entries with hours or descriptions.');
      setIsSubmitting(false);
      return;
    }

    // Validate that no day exceeds 8 hours
    if (Array.isArray(data.entries) && data.entries.length > 0) {
      const hoursPerDay = {};
      data.entries.forEach(entry => {
        if (entry.entryDate && entry.hoursWorked) {
          const hours = parseFloat(entry.hoursWorked);
          if (!Number.isNaN(hours) && hours > 0) {
            const date = entry.entryDate;
            hoursPerDay[date] = (hoursPerDay[date] || 0) + hours;
          }
        }
      });

      // Check if any day exceeds 8 hours
      const exceededDays = Object.entries(hoursPerDay).filter(([date, totalHours]) => totalHours > 8);
      if (exceededDays.length > 0) {
        const [date, totalHours] = exceededDays[0];
        toast.error(`Cannot exceed 8 hours per day. Date ${date} has ${totalHours.toFixed(2)} hours total.`);
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // If submitting REJECTED or revoked timesheet, set status to REUPDATED
      if (shouldSubmit && canSubmitRejectedOrRevoked) {
        data.status = 'REUPDATED';
      }
      await updateMutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitForApproval = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to submit this timesheet for approval?')) {
      setIsSubmitting(true);
      try {
        // For REJECTED and revoked timesheets, use update with submit flag
        if (canSubmitRejectedOrRevoked) {
          const formData = watch();
          await onSubmit(formData, true);
        } else {
          // For regular DRAFT, use the submit endpoint
          await submitMutation.mutateAsync();
        }
      } catch (error) {
        console.error('Error submitting timesheet:', error);
        toast.error('Failed to submit timesheet');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error loading timesheet: {error.response?.data?.message || error.message}</p>
        <button 
          onClick={() => navigate('/timesheets')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Timesheets
        </button>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Timesheet not found</p>
        <button 
          onClick={() => navigate('/timesheets')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Timesheets
        </button>
      </div>
    );
  }

  // Safety check for timesheet data
  if (!timesheet.startDate || !timesheet.endDate) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Invalid timesheet data - missing date information</p>
        <div className="mt-4 text-sm text-gray-400">
          <p>Timesheet ID: {id}</p>
          <p>Start Date: {timesheet.startDate || 'Missing'}</p>
          <p>End Date: {timesheet.endDate || 'Missing'}</p>
          <p>Status: {timesheet.status || 'Unknown'}</p>
          <p>Total Hours: {timesheet.totalHours || 'Unknown'}</p>
        </div>
        <button 
          onClick={() => navigate('/timesheets')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Timesheets
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0"
      >
        <div className="flex items-center space-x-3 md:space-x-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/timesheets')}
            className="p-2 md:p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 md:w-5 md:h-5" />
          </motion.button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 md:space-x-0 md:block">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Edit Timesheet</h1>
              <span className={`md:hidden px-2.5 py-1 text-xs font-medium rounded-full ${
                timesheet.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                timesheet.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800' :
                timesheet.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                timesheet.status === 'REUPDATED' ? 'bg-blue-100 text-blue-800' :
                timesheet.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {timesheet.status}
              </span>
            </div>
            <p className="text-sm md:text-base text-gray-600 mt-1">
              {safeFormatDate(timesheet.startDate, 'MMM dd')} - {safeFormatDate(timesheet.endDate, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>
        
        <div className="hidden md:flex items-center space-x-2">
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
            timesheet.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
            timesheet.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800' :
            timesheet.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
            timesheet.status === 'REUPDATED' ? 'bg-blue-100 text-blue-800' :
            timesheet.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
            'bg-red-100 text-red-800'
          }`}>
            {timesheet.status}
          </span>
        </div>
      </motion.div>

      {/* PENDING Status Notification - Mobile Optimized */}
      {timesheet.status === 'PENDING' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 md:p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm md:text-sm font-medium text-yellow-800 mb-2">
                Timesheet Rejected - Please Make Corrections
              </h3>
              <p className="text-sm text-yellow-700 mb-2">
                Your timesheet has been rejected by your supervisor. Please review the feedback below, 
                make the necessary corrections, and save to resubmit for approval.
              </p>
              {timesheet.rejectionReason && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <h4 className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</h4>
                  <p className="text-sm text-red-700 break-words">{timesheet.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* REUPDATED Status Notification - Mobile Optimized */}
      {timesheet.status === 'REUPDATED' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-blue-800 mb-2">
                Timesheet Updated & Resubmitted
              </h3>
              <p className="text-sm text-blue-700">
                Your timesheet has been updated and resubmitted for approval. It is now waiting for 
                supervisor review. You can continue to make changes if needed.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* REJECTED Status Notification - Mobile Optimized */}
      {timesheet.status === 'REJECTED' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 md:p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-red-800 mb-2">
                Timesheet Rejected - Please Make Corrections
              </h3>
              <p className="text-sm text-red-700 mb-2">
                Your timesheet has been rejected. Please review the feedback below, make the necessary corrections 
                (including dates, entries, hours, and descriptions), and save to resubmit for approval.
              </p>
              {timesheet.rejectionReason && (
                <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                  <h4 className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</h4>
                  <p className="text-sm text-red-700 break-words">{timesheet.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Revoked DRAFT Status Notification - Mobile Optimized */}
      {isRevokedDraft && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-50 border border-orange-200 rounded-lg p-4 md:p-4"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-orange-800 mb-2">
                Timesheet Revoked to Draft - Please Make Corrections
              </h3>
              <p className="text-sm text-orange-700 mb-2">
                Your timesheet has been revoked back to draft status. Please review the revocation reason below, 
                make the necessary corrections (including dates, entries, hours, and descriptions), and save to resubmit for approval.
              </p>
              {timesheet.revocationReason && (
                <div className="mt-3 p-3 bg-orange-100 border border-orange-300 rounded">
                  <h4 className="text-sm font-medium text-orange-800 mb-1">Revocation Reason:</h4>
                  <p className="text-sm text-orange-700 break-words">{timesheet.revocationReason}</p>
                  {timesheet.revokedBy && (
                    <p className="text-xs text-orange-600 mt-2">
                      Revoked by: {timesheet.revokedBy?.firstName || ''} {timesheet.revokedBy?.lastName || ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Today Task Button - Mobile Optimized */}
      {canEdit && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="px-4 md:px-0 mb-4"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                const today = getTodayDate();
                const startDate = watchedStartDate || timesheet?.startDate;
                const endDate = watchedEndDate || timesheet?.endDate;
                
                if (!startDate || !endDate) {
                  toast.error('Please set start and end dates first');
                  return;
                }
                
                const start = new Date(startDate);
                const end = new Date(endDate);
                const todayDate = new Date(today);
                
                // Check if today is within the timesheet date range
                if (todayDate >= start && todayDate <= end) {
                  addEntryForDate(today);
                  toast.success('Task added for today');
                  // Scroll to today's date section after a short delay
                  setTimeout(() => {
                    const todayElement = document.querySelector(`[data-date="${today}"]`);
                    if (todayElement) {
                      todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 300);
                } else {
                  toast.error(`Today's date (${format(todayDate, 'MMM dd, yyyy')}) is not within the timesheet period (${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')})`);
                }
              } catch (error) {
                console.error('Error adding today task:', error);
                toast.error('Failed to add task for today');
              }
            }}
            className="w-full md:w-auto px-6 py-3.5 md:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 transition-all flex items-center justify-center space-x-2 text-base md:text-sm font-bold shadow-lg md:shadow-md touch-manipulation"
          >
            <Plus className="w-5 h-5 md:w-4 md:h-4" />
            <span>Today Task</span>
          </motion.button>
        </motion.div>
      )}

      <form id="timesheet-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4 md:space-y-6">
        {/* Basic Information - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-4 md:p-6"
        >
          <h2 className="text-base md:text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm md:text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Start Date
              </label>
              <input
                {...register('startDate', { required: 'Start date is required' })}
                type="date"
                className="w-full px-4 py-3 md:py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                disabled={!canEdit}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600 font-medium">{errors.startDate.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm md:text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                End Date
              </label>
              <input
                {...register('endDate', { required: 'End date is required' })}
                type="date"
                className="w-full px-4 py-3 md:py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                disabled={!canEdit}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600 font-medium">{errors.endDate.message}</p>
              )}
            </div>
          </div>
          
          <div className="mt-4 md:mt-6">
            <label className="block text-sm md:text-sm font-semibold text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Description (Optional)
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-4 py-3 md:py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm resize-none disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
              placeholder="Add any additional notes or comments..."
              disabled={!canEdit}
            />
          </div>
        </motion.div>

        {/* Time Entries - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-4 md:p-6"
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-0 mb-4">
            <h2 className="text-base md:text-lg font-medium text-gray-900">Time Entries</h2>
            <div className="text-sm font-medium text-gray-600">
              Total Hours: <span className="text-gray-900 font-semibold">{getTotalHours()}</span>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            {getDateRange().map((date, dateIndex) => {
              const entriesForDate = getEntriesByDate()[date] || [];
              const totalHoursForDate = entriesForDate.reduce(
                (total, entry) => total + (parseFloat(entry?.hoursWorked) || 0),
                0
              );

              return (
                <motion.div
                  key={date}
                  data-date={date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: dateIndex * 0.1 }}
                  className="border border-gray-200 rounded-lg p-4 md:p-4"
                >
                  {/* Mobile: Stacked, Desktop: Horizontal */}
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:space-x-3">
                      <h3 className="text-base md:text-lg font-medium text-gray-900">
                        {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs md:text-sm font-medium w-fit">
                          {totalHoursForDate.toFixed(1)} hours
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs md:text-sm font-semibold w-fit ${
                          (8 - totalHoursForDate) >= 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {Math.max(0, (8 - totalHoursForDate).toFixed(1))}h remaining
                        </span>
                      </div>
                    </div>
                    {canEdit && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={() => addEntryForDate(date)}
                        className="w-full md:w-auto px-4 py-3 md:py-1 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 transition-all flex items-center justify-center space-x-2 text-sm font-semibold shadow-md md:shadow-none touch-manipulation"
                      >
                        <Plus className="w-5 h-5 md:w-4 md:h-4" />
                        <span>Add Task</span>
                      </motion.button>
                    )}
                  </div>

                  <div className="space-y-3 md:space-y-3">
                    {entriesForDate.map((entry, entryIndex) => {
                      // Calculate remaining hours excluding current entry
                      const currentEntryHours = parseFloat(entry?.hoursWorked) || 0;
                      const otherEntriesHours = totalHoursForDate - currentEntryHours;
                      const remainingHours = 8 - otherEntriesHours;
                      
                      return (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: entryIndex * 0.1 }}
                        className="p-3 md:p-3 bg-gray-50 rounded-lg"
                      >
                        {/* Mobile: Stacked layout, Desktop: Horizontal */}
                        <div className="flex flex-col md:flex-row md:items-start gap-4 mb-4">
                          <div className="flex-1 md:flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-semibold text-gray-700">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Hours
                              </label>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                remainingHours >= 0 
                                  ? 'bg-green-50 text-green-700' 
                                  : 'bg-red-50 text-red-700'
                              }`}>
                                {Math.max(0, remainingHours.toFixed(1))}h remaining
                              </span>
                            </div>
                            <input
                              {...register(`entries.${entry.index}.hoursWorked`, {
                                required: 'Hours is required',
                                min: { value: 0, message: 'Hours must be positive' },
                                max: { value: 24, message: 'Hours cannot exceed 24' },
                              })}
                              type="number"
                              step="0.5"
                              min="0"
                              max="24"
                              inputMode="decimal"
                              className={`w-full px-4 py-3 md:py-2 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-all ${
                                (8 - totalHoursForDate) < 0 ? 'border-red-300' : 'border-gray-200'
                              }`}
                              disabled={!canEdit}
                              placeholder="0.0"
                            />
                            {errors.entries?.[entry.index]?.hoursWorked && (
                              <p className="mt-1 text-sm text-red-600 font-medium">
                                {errors.entries[entry.index].hoursWorked.message}
                              </p>
                            )}
                            {(8 - totalHoursForDate) < 0 && (
                              <p className="mt-1 text-xs text-red-600 font-medium">
                                ⚠️ This day exceeds 8 hours ({totalHoursForDate.toFixed(1)}h total)
                              </p>
                            )}
                            {remainingHours >= 0 && remainingHours < 1 && (
                              <p className="mt-1 text-xs text-orange-600 font-medium">
                                💡 Only {remainingHours.toFixed(1)}h remaining for this day
                              </p>
                            )}
                          </div>

                          <div className="flex-[2] md:flex-[2]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Project *</label>
                            {projectsLoading ? (
                              <div className="w-full px-4 py-3 md:py-2 border-2 border-gray-200 rounded-xl text-gray-500 text-base md:text-sm">Loading projects...</div>
                            ) : projectsError ? (
                              <div className="w-full px-4 py-3 md:py-2 border-2 border-red-300 rounded-xl text-red-500 text-base md:text-sm">Error loading projects</div>
                            ) : (
                              <select
                                {...register(`entries.${entry.index}.projectId`, { 
                                  required: 'Project is required' 
                                })}
                                className={`w-full px-4 py-3 md:py-2 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed transition-all ${errors.entries?.[entry.index]?.projectId ? 'border-red-500' : 'border-gray-200'}`}
                                disabled={!canEdit}
                                value={watch(`entries.${entry.index}.projectId`) || ''}
                                onChange={(e) => {
                                  const selectedProject = projects?.find(
                                    (p) => p.id === parseInt(e.target.value)
                                  );
                                  register(`entries.${entry.index}.projectId`).onChange(e);
                                  if (selectedProject) {
                                    const projectNameField = document.querySelector(
                                      `input[name="entries.${entry.index}.projectName"]`
                                    );
                                    if (projectNameField) {
                                      projectNameField.value = selectedProject.name;
                                    }
                                  }
                                }}
                              >
                                <option value="">Select a project</option>
                                {projects?.map((project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {errors.entries?.[entry.index]?.projectId && (
                              <p className="mt-1 text-sm text-red-600 font-medium">
                                {errors.entries[entry.index].projectId.message}
                              </p>
                            )}
                            <input {...register(`entries.${entry.index}.projectName`)} type="hidden" />
                          </div>

                          <div className="flex items-end md:items-end">
                            {canEdit && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Remove this entry?')) {
                                    handleRemoveEntry(entry.index);
                                  }
                                }}
                                className="p-3 md:p-2 text-red-600 hover:text-red-800 active:text-red-900 transition-all rounded-xl hover:bg-red-50 active:bg-red-100 touch-manipulation"
                              >
                                <Trash2 className="w-6 h-6 md:w-4 md:h-4" />
                              </motion.button>
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 mb-2">
                            <label className="block text-sm font-medium text-gray-700">Task Description</label>
                            {canEdit && (
                              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
                                <button
                                  type="button"
                                  onClick={() => toggleDescriptionFormat(entry.index, 'paragraph')}
                                  className={`px-3 py-1.5 md:py-1 rounded-md text-xs font-medium transition-colors ${
                                    getDescriptionFormat(entry.index) === 'paragraph'
                                      ? 'bg-white text-primary-600 shadow-sm'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                  title="Paragraph format"
                                >
                                  <AlignLeft className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleDescriptionFormat(entry.index, 'bullets')}
                                  className={`px-3 py-1.5 md:py-1 rounded-md text-xs font-medium transition-colors ${
                                    getDescriptionFormat(entry.index) === 'bullets'
                                      ? 'bg-white text-primary-600 shadow-sm'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                  title="Bullet points format"
                                >
                                  <List className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          <textarea
                            {...register(`entries.${entry.index}.taskDescription`)}
                            rows={4}
                            className="w-full px-4 py-3 md:py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm resize-none disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                            placeholder={
                              getDescriptionFormat(entry.index) === 'bullets'
                                ? 'Enter each task on a new line:\n• Task 1\n• Task 2\n• Task 3'
                                : 'What did you work on?'
                            }
                            disabled={!canEdit}
                          />
                          {getDescriptionFormat(entry.index) === 'bullets' && canEdit && (
                            <p className="text-xs text-gray-500 mt-1">
                              💡 Tip: Start each line with • or - for bullet points
                            </p>
                          )}
                        </div>
                      </motion.div>
                      );
                    })}

                    {entriesForDate.length === 0 && (
                      <div className="text-center py-8 md:py-8 text-gray-500">
                        <div className="w-16 h-16 md:w-12 md:h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                          <Clock className="h-8 w-8 md:h-6 md:w-6 text-gray-400" />
                        </div>
                        <p className="text-sm md:text-base font-medium mb-1">No tasks added for this date</p>
                        <p className="text-xs text-gray-400 mb-4">Tap "Add Task" above to get started</p>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => addEntryForDate(date)}
                            className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 transition-all text-sm font-semibold shadow-md active:scale-95 touch-manipulation"
                          >
                            Add Your First Task
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Actions - Mobile Optimized with Better UX */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col-reverse md:flex-row md:justify-end gap-3 md:space-x-4 bg-white pt-6 pb-6 md:pt-4 md:pb-4 px-4 md:px-0 border-t-2 md:border-t border-gray-200 mt-6 md:mt-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate('/timesheets');
            }}
            className="w-full md:w-auto px-4 py-3.5 md:py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all text-base md:text-sm font-semibold touch-manipulation"
          >
            Cancel
          </motion.button>
          
          {canEdit && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={isSubmitting || updateMutation.isLoading}
              className="w-full md:w-auto px-4 py-3.5 md:py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-base md:text-sm font-semibold shadow-md md:shadow-none touch-manipulation"
            >
              {isSubmitting || updateMutation.isLoading ? (
                <div className="spinner"></div>
              ) : (
                <>
                  <Save className="w-5 h-5 md:w-4 md:h-4" />
                  <span>
                    {timesheet.status === 'PENDING' ? 'Save & Resubmit' : 'Save Changes'}
                  </span>
                </>
              )}
            </motion.button>
          )}
          
          {(canSubmit || canSubmitRejectedOrRevoked) && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
            onClick={submitForApproval}
              disabled={isSubmitting || submitMutation.isLoading || updateMutation.isLoading}
              className="w-full md:w-auto px-4 py-3.5 md:py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-base md:text-sm font-semibold shadow-md md:shadow-none touch-manipulation"
            >
              {(isSubmitting || submitMutation.isLoading || updateMutation.isLoading) ? (
                <div className="spinner"></div>
              ) : (
                <>
                  <Send className="w-5 h-5 md:w-4 md:h-4" />
                  <span>
                    {canSubmitRejectedOrRevoked ? 'Save & Submit' : 'Submit for Approval'}
                  </span>
                </>
              )}
            </motion.button>
          )}
        </motion.div>
      </form>
    </div>
  );
};

export default EditTimesheet;

